import { Download, FileText } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { formatBytes, formatMoney } from "../utils/format.ts";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import CountryName from "../components/CountryName";
import CpvCodeList from "../components/CpvCode";
import DetailRow from "../components/DetailRow.tsx";
import EmptyState from "../components/EmptyState.tsx";
import FormattedDate from "../components/FormattedDate";
import IdentityCell from "../components/IdentityCell.tsx";
import Panel from "../components/Panel.tsx";
import SectionCard from "../components/SectionCard.tsx";
import StatusChip from "../components/StatusChip.tsx";
import DescriptionBlock from "../components/detail/DescriptionBlock.tsx";
import DetailHeader from "../components/detail/DetailHeader.tsx";
import DetailPageStatus from "../components/detail/DetailPageStatus.tsx";
import { matchSummary } from "../components/detail/MatchedCompanyCard.tsx";
import SourceDataCard from "../components/detail/SourceDataCard.tsx";
import { useTenant } from "../contexts/TenantContext.tsx";
import { ensureHttps } from "../utils/url.ts";

const BACK = { to: "/tenders", label: "Tenders" };

interface PartyPanelProps {
  title: string;
  name: string | null;
  companyId: string | null;
  companyName: string | null;
  orgNumber: string | null;
  country: string | null;
  matchMethod: string | null;
  matchConfidence: number | null;
  children?: ReactNode;
}

// The buyer or winner: linked to its master company when matched.
function PartyPanel({
  title,
  name,
  companyId,
  companyName,
  orgNumber,
  country,
  matchMethod,
  matchConfidence,
  children,
}: PartyPanelProps) {
  const { tenantPath } = useTenant();
  const match = matchSummary(matchMethod, matchConfidence, "Match");
  return (
    <Panel title={title} fill>
      <Box sx={{ mb: 1.5 }}>
        <IdentityCell
          name={(companyId ? companyName : null) ?? name ?? "Unknown"}
          to={companyId ? tenantPath(`/companies/${companyId}`) : undefined}
          caption={match ?? (companyId ? undefined : "Not matched to a company")}
        />
      </Box>
      <DetailRow label="Org number" value={orgNumber} />
      <DetailRow label="Country" value={country ? <CountryName code={country} /> : null} />
      {children}
    </Panel>
  );
}

function TenderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: tender, isLoading, isError } = useQuery({
    queryKey: keys.tender.detail(id),
    queryFn: () => api.getTender(id!),
    enabled: !!id,
  });

  const fetchDocsMutation = useMutation({
    mutationFn: () => api.fetchTenderDocuments(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.tender.detail(id) });
      toast.success("Fetching documents from the source portal");
    },
  });

  if (isLoading || isError || !tender) {
    return <DetailPageStatus isLoading={isLoading} message="Tender not found" back={BACK} />;
  }

  const hasWinner = !!(tender.winner_name || tender.winner_company_id);
  const documents = tender.documents ?? [];

  const money = (value: string | null) =>
    value ? formatMoney(value, tender.currency, { decimals: 0 }) : null;
  const estimated = money(tender.estimated_value);
  const awarded = money(tender.awarded_value);

  return (
    <>
      <DetailHeader
        back={BACK}
        title={tender.title}
        sourceUrl={ensureHttps(tender.source_url)}
        chips={
          <>
            <Chip size="small" label={tender.source.toUpperCase()} />
            <StatusChip kind="tender_notice_type" value={tender.notice_type} />
            {tender.notice_subtype && <Chip size="small" label={tender.notice_subtype} variant="outlined" />}
            {tender.is_framework_agreement && <Chip size="small" label="Framework agreement" variant="outlined" />}
            {tender.country && (
              <Chip size="small" label={<CountryName code={tender.country} />} variant="outlined" />
            )}
          </>
        }
      />

      <SectionCard title="Notice">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow label="Notice ID" value={tender.notice_id} />
            <DetailRow label="Procedure ID" value={tender.procedure_id} />
            <DetailRow label="Version" value={tender.version} />
            <DetailRow label="Procedure type" value={tender.procedure_type} />
            <DetailRow label="Language" value={tender.language} />
            <DetailRow
              label="CPV codes"
              value={
                tender.cpv_codes && tender.cpv_codes.length > 0 ? (
                  <CpvCodeList codes={tender.cpv_codes} />
                ) : null
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow label="Estimated value" value={estimated} />
            <DetailRow label="Awarded value" value={awarded} />
            <DetailRow
              label="Published"
              value={tender.publication_date ? <FormattedDate value={tender.publication_date} /> : null}
            />
            <DetailRow
              label="Submission deadline"
              value={tender.submission_deadline ? <FormattedDate value={tender.submission_deadline} /> : null}
            />
            <DetailRow
              label="Award date"
              value={tender.award_date ? <FormattedDate value={tender.award_date} /> : null}
            />
            <DetailRow
              label="Contract signature"
              value={
                tender.contract_signature_date ? (
                  <FormattedDate value={tender.contract_signature_date} />
                ) : null
              }
            />
            <DetailRow label="Tenders received" value={tender.num_tenders_received} />
          </Grid>
        </Grid>
      </SectionCard>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: hasWinner ? 6 : 12 }}>
          <PartyPanel
            title="Buyer"
            name={tender.buyer_name}
            companyId={tender.buyer_company_id}
            companyName={tender.buyer_company_name}
            orgNumber={tender.buyer_org_number}
            country={tender.buyer_country}
            matchMethod={tender.buyer_match_method}
            matchConfidence={tender.buyer_match_confidence}
          />
        </Grid>
        {hasWinner && (
          <Grid size={{ xs: 12, md: 6 }}>
            <PartyPanel
              title="Winner"
              name={tender.winner_name}
              companyId={tender.winner_company_id}
              companyName={tender.winner_company_name}
              orgNumber={tender.winner_org_number}
              country={tender.winner_country}
              matchMethod={tender.winner_match_method}
              matchConfidence={tender.winner_match_confidence}
            >
              <DetailRow
                label="SME"
                value={tender.winner_is_sme === null ? null : tender.winner_is_sme ? "Yes" : "No"}
              />
            </PartyPanel>
          </Grid>
        )}
      </Grid>

      {tender.description && (
        <SectionCard title="Description">
          <DescriptionBlock>{tender.description}</DescriptionBlock>
        </SectionCard>
      )}

      <Panel
        title="Documents"
        count={documents.length > 0 ? documents.length : undefined}
        sx={{ mb: 3 }}
        action={
          <Button
            size="small"
            variant="outlined"
            onClick={() => fetchDocsMutation.mutate()}
            loading={fetchDocsMutation.isPending}
          >
            Fetch documents
          </Button>
        }
      >
        {documents.length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title="No documents fetched yet"
            description="Fetch documents to try pulling them from the source portal."
          />
        ) : (
          <List dense disablePadding>
            {documents.map((doc) => {
              const subtitle = [
                doc.mime_type,
                formatBytes(doc.size_bytes),
                doc.fetch_method,
              ]
                .filter(Boolean)
                .join(" · ");
              const isFetched = doc.fetch_status === "fetched" && doc.download_url;
              return (
                <ListItem
                  key={doc.id}
                  disableGutters
                  secondaryAction={
                    isFetched ? (
                      <Link
                        href={doc.download_url!}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Download fontSize="small" /> Download
                      </Link>
                    ) : (
                      <Chip
                        label={doc.fetch_status === "metadata_only" ? "Not downloadable" : doc.fetch_status}
                        size="small"
                        variant="outlined"
                      />
                    )
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FileText fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.filename ?? doc.external_url}
                    secondary={subtitle || undefined}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Panel>

      <SourceDataCard data={tender.source_data} />
    </>
  );
}

export default TenderDetailPage;
